import { bearerFetcher } from "matsuri-hooks"
import {
    isRetryableUploadError,
    uploadWithRetry
} from "@/utils/uploadWithRetry"

interface IssueResponse {
    uploadId: string
    uploadUrl: string
}

interface PresignedUploadParams {
    token: string | undefined
    issueUrl: string
    completeUrl: string
    blob: Blob
    // complete エンドポイントが Body を返さない（void）場合は false にする。デフォルトは true。
    expectJsonResponse?: boolean
    // complete を fetch で送る代わりに、呼び出し側で送る場合に使うフック。
    // 戻り値で T が返ってきたらそれを処理、undefined ならスキップ。
    sendComplete?: (url: string, body: BodyInit) => Promise<unknown | undefined>
}

const fallbackContentType = (blob: Blob): string =>
    blob.type || "application/octet-stream"

const throwIfFetcherError = <T>(
    result: { data: T | undefined; error: { status?: number } | undefined },
    label: string
): T => {
    if (result.error) {
        throw Object.assign(new Error(`${label} failed`), {
            status: result.error.status
        })
    }
    if (result.data === undefined) {
        throw new Error(`${label} returned no data`)
    }
    return result.data
}

// Presigned URL を発行 → S3 へ直接 PUT → 完了通知、の3段階を共通化したヘルパー。
// 戻り値は complete エンドポイントのレスポンス JSON。
// expectJsonResponse=true（デフォルト）の場合は JSON が取得できなければ throw する。
// expectJsonResponse=false の場合は void として undefined を返す。
export const uploadWithPresignedUrl = async <T = unknown>({
    token,
    issueUrl,
    completeUrl,
    blob,
    expectJsonResponse = true,
    sendComplete
}: PresignedUploadParams): Promise<T | undefined> => {
    const contentType = fallbackContentType(blob)

    // 1) Presigned URL 発行
    const issued = await uploadWithRetry(async () => {
        const result = await bearerFetcher<IssueResponse>(
            token ?? "",
            issueUrl,
            {
                method: "POST",
                body: JSON.stringify({})
            }
        )
        return throwIfFetcherError(result, "issue upload url")
    })

    // 2) S3 への直接 PUT（presigned URL の署名と Authorization ヘッダーが衝突するため生 fetch）
    await uploadWithRetry(async () => {
        // eslint-disable-next-line no-restricted-globals
        const r = await fetch(issued.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": contentType },
            body: blob
        })
        if (!r.ok) {
            throw Object.assign(new Error(`s3 put failed: ${r.status}`), {
                status: r.status
            })
        }
        return r
    })

    // 3) 完了通知
    const completeBody = JSON.stringify({ uploadId: issued.uploadId })
    const completed: T | undefined = sendComplete
        ? ((await sendComplete(completeUrl, completeBody)) as T | undefined)
        : await uploadWithRetry(async () => {
              const result = await bearerFetcher<T>(token ?? "", completeUrl, {
                  method: "POST",
                  body: completeBody
              })
              if (result.error) {
                  throw Object.assign(new Error(`complete failed`), {
                      status: result.error.status
                  })
              }
              return result.data
          })

    if (completed === undefined) {
        if (expectJsonResponse) {
            throw new Error("complete response body is empty")
        }
        return undefined
    }
    return completed
}

// issue + S3 PUT だけを行う。complete は呼び出し側で別途送る用途（complete に追加フィールドが必要なケース等）。
// 戻り値の uploadId を呼び出し側が complete のリクエストに含める想定。
// signal を渡すと、撮影中の × による中断（AbortController.abort()）で S3 PUT をキャンセルできる。
export const uploadBlobWithPresignedPut = async ({
    token,
    issueUrl,
    blob,
    signal
}: {
    token: string | undefined
    issueUrl: string
    blob: Blob
    signal?: AbortSignal
}): Promise<{ uploadId: string }> => {
    const contentType = fallbackContentType(blob)

    const issued = await uploadWithRetry(async () => {
        if (signal?.aborted) {
            throw new DOMException("aborted", "AbortError")
        }
        const result = await bearerFetcher<IssueResponse>(
            token ?? "",
            issueUrl,
            {
                method: "POST",
                body: JSON.stringify({})
            }
        )
        return throwIfFetcherError(result, "issue upload url")
    })

    await uploadWithRetry(async () => {
        if (signal?.aborted) {
            throw new DOMException("aborted", "AbortError")
        }
        // eslint-disable-next-line no-restricted-globals
        const r = await fetch(issued.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": contentType },
            body: blob,
            signal
        })
        if (!r.ok) {
            throw Object.assign(new Error(`s3 put failed: ${r.status}`), {
                status: r.status
            })
        }
        return r
    })

    return { uploadId: issued.uploadId }
}

export { isRetryableUploadError }
